import { MetadataProductCard } from '../../types/cmsTypes';

/**
 * Editorial row labels (CMS-1906). Each ProductMetaConfig product entry carries five on/off toggles;
 * the comparison table shows the resolved label as a rotated word in a 24px rail left of BANK_LOGO.
 */
export type RowLabel =
  | 'OUR_PICK'
  | 'ENDS_SOON'
  | 'FLASH_DEAL'
  | 'NEW'
  | 'EXCLUSIVE';

export type RowLabelToggles = {
  label_our_pick?: boolean;
  label_new?: boolean;
  label_ends_soon?: boolean;
  label_flash_deal?: boolean;
  label_exclusive?: boolean;
};

/**
 * Selection order when more than `MAX_CAPPED_ROW_LABELS` products qualify. EXCLUSIVE is uncapped and
 * sits last only to break the tie when an entry has several toggles on (the CMS rejects that; seeded
 * data may still carry it).
 */
const ROW_LABEL_PRIORITY: RowLabel[] = [
  'OUR_PICK',
  'ENDS_SOON',
  'FLASH_DEAL',
  'NEW',
  'EXCLUSIVE',
];

const CAPPED_ROW_LABELS: RowLabel[] = ROW_LABEL_PRIORITY.filter(
  (label) => label !== 'EXCLUSIVE',
);

export const MAX_CAPPED_ROW_LABELS = 3;

const TOGGLE_BY_LABEL: Record<RowLabel, keyof RowLabelToggles> = {
  OUR_PICK: 'label_our_pick',
  ENDS_SOON: 'label_ends_soon',
  FLASH_DEAL: 'label_flash_deal',
  NEW: 'label_new',
  EXCLUSIVE: 'label_exclusive',
};

export const getRowLabelFromToggles = (
  toggles: RowLabelToggles | undefined,
): RowLabel | undefined =>
  ROW_LABEL_PRIORITY.find((label) => toggles?.[TOGGLE_BY_LABEL[label]]);

/**
 * Resolves the label each ProductMetaConfig entry renders, aligned by index with `cards`. At most
 * `MAX_CAPPED_ROW_LABELS` non-EXCLUSIVE labels per ProductMetaConfig (the page-level product list
 * every comparison table on the page shares), taken in `ROW_LABEL_PRIORITY` then entry order;
 * entries past the cap get `undefined`. Pass the entries that render rows, before tab/filter/sort
 * state — the user's view must not change who is labelled. The PERSONAL_LOANS table may still drop
 * a labelled row later (its required-attribute filter reacts to the calculator); by design that row
 * takes its label with it and the slot is not reassigned, so a recalculation never moves a label
 * between products (requirement 3.2) even if it leaves fewer than three rails.
 */
export const resolveRowLabels = (
  cards: ReadonlyArray<Pick<MetadataProductCard, 'productBlock'>>,
): Array<RowLabel | undefined> => {
  const wanted = cards.map((card) => getRowLabelFromToggles(card.productBlock));
  const result: Array<RowLabel | undefined> = wanted.map((label) =>
    label === 'EXCLUSIVE' ? label : undefined,
  );

  let cappedCount = 0;
  CAPPED_ROW_LABELS.forEach((label) => {
    wanted.forEach((wantedLabel, index) => {
      if (wantedLabel !== label || cappedCount >= MAX_CAPPED_ROW_LABELS) {
        return;
      }
      result[index] = label;
      cappedCount += 1;
    });
  });

  return result;
};
